import type { SyncItem } from '../api/types'
import { isIsoDate, isoWeekday } from '../lib/days'
import type { MacroTargets } from '../lib/nutrition'
import { latestById } from './cache'
import { asRecord, bool, int, isRecord, num, oneOf, str, strings, weekdays, type JsonObject } from './read'

// GOAL -------------------------------------------------------------------------------------------

export const GOAL_TYPES = ['LOSE', 'MAINTAIN', 'GAIN'] as const
export type GoalType = (typeof GOAL_TYPES)[number]

export const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  LOSE: 'Lose weight',
  MAINTAIN: 'Maintain weight',
  GAIN: 'Gain weight',
}

/** One goal period: a GOAL item. The current goal is the one with the latest start date. */
export interface Goal {
  id: string
  updatedAt: number
  type: GoalType
  startDate: string
  startWeightKg: number | null
  targetWeightKg: number | null
  /** % of body weight per week: negative to lose, positive to gain, 0 to maintain. */
  ratePctPerWeek: number
  raw: JsonObject
}

/** Usable goals (a known type and a valid start date), latest start first. */
export function parseGoals(items: readonly SyncItem[]): Goal[] {
  const goals: Goal[] = []
  for (const item of latestById(items, 'GOAL')) {
    if (item.deleted) continue
    const type = item.data.type
    const startDate = str(item.data.startDate)
    if (!(GOAL_TYPES as readonly unknown[]).includes(type) || startDate === null || !isIsoDate(startDate)) continue
    const startWeight = num(item.data.startWeightKg)
    const targetWeight = num(item.data.targetWeightKg)
    goals.push({
      id: item.id,
      updatedAt: item.updatedAt,
      type: type as GoalType,
      startDate,
      startWeightKg: startWeight !== null && startWeight > 0 ? startWeight : null,
      targetWeightKg: targetWeight !== null && targetWeight > 0 ? targetWeight : null,
      ratePctPerWeek: num(item.data.ratePctPerWeek) ?? 0,
      raw: item.data,
    })
  }
  return goals.sort((a, b) => b.startDate.localeCompare(a.startDate) || b.updatedAt - a.updatedAt)
}

export function goalItem(goal: Goal, now: number = Date.now()): SyncItem {
  return {
    type: 'GOAL',
    id: goal.id,
    updatedAt: now,
    deleted: false,
    data: {
      ...goal.raw,
      type: goal.type,
      startDate: goal.startDate,
      startWeightKg: goal.startWeightKg,
      targetWeightKg: goal.targetWeightKg,
      ratePctPerWeek: goal.ratePctPerWeek,
    },
  }
}

export function goalTombstone(id: string, now: number = Date.now()): SyncItem {
  return { type: 'GOAL', id, updatedAt: now, deleted: true, data: {} }
}

/** The rate's sign follows the goal: losing is negative, gaining positive, maintaining zero. */
export function signedRate(type: GoalType, ratePct: number): number {
  const size = Math.abs(ratePct)
  if (type === 'MAINTAIN') return 0
  return type === 'LOSE' ? -size : size
}

/** kg/week for a % rate: rate × trend weight / 100 (sync contract, GOAL). */
export function kgPerWeek(ratePctPerWeek: number, trendWeightKg: number): number {
  return (ratePctPerWeek * trendWeightKg) / 100
}

// PROGRAM ----------------------------------------------------------------------------------------

export const PROGRAM_KINDS = ['COACHED', 'COLLABORATIVE', 'MANUAL'] as const
export type ProgramKind = (typeof PROGRAM_KINDS)[number]
export const DIET_TYPES = ['BALANCED', 'LOW_FAT', 'LOW_CARB', 'KETO'] as const
export type DietType = (typeof DIET_TYPES)[number]
export const PROTEIN_PREFERENCES = ['LOW', 'MODERATE', 'HIGH', 'EXTRA_HIGH'] as const
export type ProteinPreference = (typeof PROTEIN_PREFERENCES)[number]
export const CALORIE_FLOORS = ['STANDARD', 'LOW', 'NONE'] as const
export type CalorieFloor = (typeof CALORIE_FLOORS)[number]
export const DISTRIBUTION_KINDS = ['EVEN', 'HIGH_DAYS'] as const
export type DistributionKind = (typeof DISTRIBUTION_KINDS)[number]

/** The nutrition program: the single PROGRAM item, id `current`. */
export interface Program {
  kind: ProgramKind
  dietType: DietType
  proteinPreference: ProteinPreference
  calorieFloor: CalorieFloor
  /** ISO weekday of the weekly check-in, 1 = Monday. */
  checkInWeekday: number
  distribution: { kind: DistributionKind; highDays: number[] }
  /** [carbs]: the shifted calories are carbohydrate only (shared TrainingShift.carbs). */
  trainingShift: { enabled: boolean; kcal: number; carbs: boolean }
  /** Daily targets used when kind is MANUAL: 7 entries Monday first, or 1 for every day. */
  manual: MacroTargets[]
  raw: JsonObject
}

export const PROGRAM_ID = 'current'

export const DEFAULT_PROGRAM: Program = {
  kind: 'COACHED',
  dietType: 'BALANCED',
  proteinPreference: 'MODERATE',
  calorieFloor: 'STANDARD',
  checkInWeekday: 1,
  distribution: { kind: 'EVEN', highDays: [] },
  trainingShift: { enabled: false, kcal: 150, carbs: false },
  manual: [],
  raw: {},
}

/** A daily target, or null unless kcal and the three macros are non-negative numbers. */
export function readTargets(value: unknown): MacroTargets | null {
  if (!isRecord(value)) return null
  const values = [num(value.kcal), num(value.protein), num(value.fat), num(value.carbs)]
  if (values.some((amount) => amount === null || amount < 0)) return null
  const [kcal, protein, fat, carbs] = values as [number, number, number, number]
  return { kcal, protein, fat, carbs }
}

/** A target list as PROGRAM.manual and CHECKIN.targets hold it: 7 valid entries or 1; otherwise empty. */
export function readTargetList(value: unknown): MacroTargets[] {
  if (!Array.isArray(value) || (value.length !== 1 && value.length !== 7)) return []
  const targets = value.map(readTargets)
  return targets.every((target) => target !== null) ? (targets as MacroTargets[]) : []
}

export function parseProgram(items: readonly SyncItem[]): Program | null {
  const item = latestById(items, 'PROGRAM').find((candidate) => candidate.id === PROGRAM_ID)
  if (!item || item.deleted) return null
  const data = item.data
  const distribution = asRecord(data.distribution)
  const shift = asRecord(data.trainingShift)
  const weekday = int(data.checkInWeekday)
  return {
    kind: oneOf(data.kind, PROGRAM_KINDS, DEFAULT_PROGRAM.kind),
    dietType: oneOf(data.dietType, DIET_TYPES, DEFAULT_PROGRAM.dietType),
    proteinPreference: oneOf(data.proteinPreference, PROTEIN_PREFERENCES, DEFAULT_PROGRAM.proteinPreference),
    calorieFloor: oneOf(data.calorieFloor, CALORIE_FLOORS, DEFAULT_PROGRAM.calorieFloor),
    checkInWeekday: weekday !== null && weekday >= 1 && weekday <= 7 ? weekday : DEFAULT_PROGRAM.checkInWeekday,
    distribution: {
      kind: oneOf(distribution.kind, DISTRIBUTION_KINDS, 'EVEN'),
      highDays: weekdays(distribution.highDays),
    },
    trainingShift: {
      enabled: bool(shift.enabled, false),
      kcal: num(shift.kcal) ?? DEFAULT_PROGRAM.trainingShift.kcal,
      carbs: bool(shift.carbs, false),
    },
    manual: readTargetList(data.manual),
    raw: data,
  }
}

const targetsData = (target: MacroTargets) => ({
  kcal: target.kcal,
  protein: target.protein,
  fat: target.fat,
  carbs: target.carbs,
})

export function programItem(program: Program, now: number = Date.now()): SyncItem {
  return {
    type: 'PROGRAM',
    id: PROGRAM_ID,
    updatedAt: now,
    deleted: false,
    data: {
      ...program.raw,
      kind: program.kind,
      dietType: program.dietType,
      proteinPreference: program.proteinPreference,
      calorieFloor: program.calorieFloor,
      checkInWeekday: program.checkInWeekday,
      distribution: {
        kind: program.distribution.kind,
        highDays: program.distribution.kind === 'HIGH_DAYS' ? [...program.distribution.highDays] : [],
      },
      // `carbs` is written once turned on (or already stored), as the phone writes it.
      trainingShift: {
        ...asRecord(program.raw.trainingShift),
        enabled: program.trainingShift.enabled,
        kcal: program.trainingShift.kcal,
        ...(program.trainingShift.carbs || 'carbs' in asRecord(program.raw.trainingShift) ? { carbs: program.trainingShift.carbs } : {}),
      },
      manual: program.manual.map(targetsData),
    },
  }
}

// CHECKIN ----------------------------------------------------------------------------------------

/** One weekly check-in: a CHECKIN item whose id is its date, written when the user accepts it. */
export interface CheckIn {
  date: string
  expenditureKcal: number | null
  trendWeightKg: number | null
  weeklyChangeKg: number | null
  rawTargetKcal: number | null
  /** 7 entries Monday first, or 1 for every day. */
  targets: MacroTargets[]
  loggingReview: { partialDays: string[]; fastedDays: string[] }
  persistence: { direction: number; weeks: number }
  accepted: boolean
  raw: JsonObject
}

/** Check-ins with a valid date, oldest first. */
export function parseCheckIns(items: readonly SyncItem[]): CheckIn[] {
  const checkIns: CheckIn[] = []
  for (const item of latestById(items, 'CHECKIN')) {
    if (item.deleted) continue
    const data = item.data
    const date = str(data.date) ?? item.id
    if (!isIsoDate(date)) continue
    const review = asRecord(data.loggingReview)
    const persistence = asRecord(data.persistence)
    checkIns.push({
      date,
      expenditureKcal: num(data.expenditureKcal),
      trendWeightKg: num(data.trendWeightKg),
      weeklyChangeKg: num(data.weeklyChangeKg),
      rawTargetKcal: num(data.rawTargetKcal),
      targets: readTargetList(data.targets),
      loggingReview: {
        partialDays: strings(review.partialDays).filter(isIsoDate),
        fastedDays: strings(review.fastedDays).filter(isIsoDate),
      },
      persistence: { direction: int(persistence.direction) ?? 0, weeks: int(persistence.weeks) ?? 0 },
      accepted: data.accepted === true,
      raw: data,
    })
  }
  return checkIns.sort((a, b) => a.date.localeCompare(b.date))
}

export function checkInItem(checkIn: CheckIn, now: number = Date.now()): SyncItem {
  return {
    type: 'CHECKIN',
    id: checkIn.date,
    updatedAt: now,
    deleted: false,
    data: {
      ...checkIn.raw,
      date: checkIn.date,
      expenditureKcal: checkIn.expenditureKcal,
      trendWeightKg: checkIn.trendWeightKg,
      weeklyChangeKg: checkIn.weeklyChangeKg,
      rawTargetKcal: checkIn.rawTargetKcal,
      targets: checkIn.targets.map(targetsData),
      loggingReview: {
        partialDays: [...checkIn.loggingReview.partialDays],
        fastedDays: [...checkIn.loggingReview.fastedDays],
      },
      persistence: { ...checkIn.persistence },
      accepted: checkIn.accepted,
    },
  }
}

// Targets for a day ------------------------------------------------------------------------------

/** The entry of a 7-day (Monday first) or 1-entry target list that applies on [date]. */
export function targetForWeekday(list: readonly MacroTargets[], date: string): MacroTargets | null {
  if (list.length === 1) return list[0] ?? null
  if (list.length === 7) return list[isoWeekday(date) - 1] ?? null
  return null
}

/**
 * A day's targets (sync contract, CHECKIN): from PROGRAM.manual when the program is MANUAL, otherwise
 * from the latest accepted check-in on or before the day. Null when neither has any. A MANUAL program
 * without targets yet reads check-ins, as WeeklyCheckIn in shared/ does, so switching to MANUAL never
 * blanks the targets before the user types them.
 */
export function targetsForDay(date: string, program: Program | null, checkIns: readonly CheckIn[]): MacroTargets | null {
  if (program?.kind === 'MANUAL' && program.manual.length > 0) return targetForWeekday(program.manual, date)
  let latest: CheckIn | null = null
  for (const checkIn of checkIns) {
    if (!checkIn.accepted || checkIn.date > date || checkIn.targets.length === 0) continue
    if (latest === null || checkIn.date > latest.date) latest = checkIn
  }
  return latest ? targetForWeekday(latest.targets, date) : null
}
